import { AppState } from '@redux/store'
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { InsuranceMemberStep, InsuranceMemberStepPayload, InsuranceMemberStepState } from '@typesApp/InsuranceMember'
import { ProviderType } from '@typesApp/Insurer'
import { ProductQuote, ProductQuoteResult } from '@typesApp/ProductQuote'
import { InsuranceActionListenerCallback, InsuranceActionListenerList } from '@typesApp/InsuranceActionListener'
import { claimRecordsApi } from '@redux/queries/claimRecords'

type ProductQuoteStatus = {
  isLoading?: boolean
  isError?: boolean
  isSuccess?: boolean
  productQuotes?: ProductQuote[]
}

type InsurerSelection = {
  providerType?: ProviderType
  isCleared?: boolean
}

type ExternalInsurerSelection = {
  insurerId?: string
  providerType?: ProviderType
}

export type InsuranceMemberState = {
  insuranceMemberStep?: InsuranceMemberStepState
  insurerSelection?: InsurerSelection
  externalInsurerSelection?: ExternalInsurerSelection
  isInsuranceToggleClicked?: boolean
  isProductQuoteError?: boolean
  insuranceActionListeners?: InsuranceActionListenerList
  productQuoteStatus?: ProductQuoteStatus
}

const initialState: InsuranceMemberState = {}

export const insuranceMemberSlice = createSlice({
  name: 'insuranceMember',
  initialState: initialState,
  reducers: {
    setInsuranceMemberStep(state, action: PayloadAction<InsuranceMemberStepPayload>) {
      state.insuranceMemberStep = action.payload
      // clear external insurer selection unless still on insurance form step
      if (action.payload.step !== InsuranceMemberStep.FIND_MEMBER) {
        state.externalInsurerSelection = undefined
      }
    },
    resetInsuranceMemberStep(state) {
      state.insuranceMemberStep = undefined
    },
    setInsuranceMemberExternalInsurerSelection(state, action: PayloadAction<ExternalInsurerSelection>) {
      state.externalInsurerSelection = action.payload

      // set provider type from external selection
      if (action.payload?.providerType) {
        state.insurerSelection = {
          providerType: action.payload.providerType,
        }
      }
    },
    setInsuranceMemberInsurerSelection(state, action: PayloadAction<ProviderType | undefined>) {
      state.insurerSelection = {
        providerType: action.payload,
        isCleared: !action.payload ? true : undefined,
      }
      // reset external selection
      state.externalInsurerSelection = undefined
    },
    restoreInsuranceMemberInsurerSelection(state, action: PayloadAction<ProviderType | undefined>) {
      // restore providerType if not cleared by user
      if (!state.insurerSelection?.providerType && !state.insurerSelection?.isCleared) {
        state.insurerSelection = {
          providerType: action.payload,
        }
      }
      // reset external selection
      state.externalInsurerSelection = undefined
    },
    setInsuranceToggleClicked(state, action: PayloadAction<boolean>) {
      state.isInsuranceToggleClicked = action.payload
    },
    resetInsuranceToggleClicked(state) {
      state.isInsuranceToggleClicked = undefined
    },
    addInsuranceActionListener(
      state,
      action: PayloadAction<{ listener: InsuranceActionListenerCallback; isLegacy?: boolean }>
    ) {
      if (!state.insuranceActionListeners) {
        state.insuranceActionListeners = {
          listeners: [],
        }
      }
      state.insuranceActionListeners.isLegacy = action.payload.isLegacy
      state.insuranceActionListeners.listeners?.push(action.payload.listener)
    },
    removeInsuranceActionListener(state, action: PayloadAction<InsuranceActionListenerCallback>) {
      if (state.insuranceActionListeners) {
        const handlers = state.insuranceActionListeners.listeners ?? []
        const remainingHandlers = handlers.filter(handler => handler !== action.payload)
        state.insuranceActionListeners.listeners = remainingHandlers
      }
    },
    setProductQuoteLoading(state) {
      state.productQuoteStatus = {
        isLoading: true,
      }
    },
    setProductQuoteError(state, action: PayloadAction<boolean>) {
      state.productQuoteStatus = {
        ...state.productQuoteStatus,
        isError: action.payload,
        isLoading: false,
      }
    },
    setProductQuoteSuccess(state, action: PayloadAction<ProductQuoteResult>) {
      const productQuotes = Array.isArray(action.payload?.productQuotes) ? action.payload.productQuotes : []

      state.productQuoteStatus = {
        ...state.productQuoteStatus,
        isSuccess: productQuotes.length > 0,
        isLoading: false,
        productQuotes,
      }
    },
    resetProductQuoteStatus(state) {
      state.productQuoteStatus = undefined
    },
    resetInsuranceMemberState(state) {
      // always retain the listeners, only commerce F/E can remove them
      // always retain the product quote status for the toggle message
      return {
        ...initialState,
        insurerSelection: state.insurerSelection,
        productQuoteStatus: state.productQuoteStatus,
        insuranceActionListeners: state.insuranceActionListeners,
      }
    },
  },
  extraReducers: builder => {
    // reset product quote status on delete claim record or toggle click
    builder.addMatcher(
      isAnyOf(
        claimRecordsApi.endpoints.deleteClaimRecord.matchFulfilled,
        claimRecordsApi.endpoints.changeInsuranceStatus.matchFulfilled
      ),
      state => {
        state.productQuoteStatus = undefined
      }
    )
  },
})

export const {
  setInsuranceMemberStep,
  resetInsuranceMemberStep,
  setInsuranceMemberInsurerSelection,
  restoreInsuranceMemberInsurerSelection,
  setInsuranceMemberExternalInsurerSelection,
  setInsuranceToggleClicked,
  resetInsuranceToggleClicked,
  addInsuranceActionListener,
  removeInsuranceActionListener,
  setProductQuoteLoading,
  setProductQuoteError,
  setProductQuoteSuccess,
  resetProductQuoteStatus,
  resetInsuranceMemberState,
} = insuranceMemberSlice.actions

export const insuranceMemberStepSelector = (state: AppState): InsuranceMemberStepState | undefined =>
  state.insuranceMember.insuranceMemberStep
export const insuranceMemberInsurerProviderTypeSelector = (state: AppState): ProviderType | undefined =>
  state.insuranceMember.insurerSelection?.providerType
export const insuranceMemberExternalInsurerSelectionSelector = (
  state: AppState
): ExternalInsurerSelection | undefined => state.insuranceMember.externalInsurerSelection
export const insuranceToggleClickedSelector = (state: AppState): boolean =>
  state.insuranceMember.isInsuranceToggleClicked === true
export const insuranceListenersSelector = (state: AppState): InsuranceActionListenerList | undefined =>
  state.insuranceMember.insuranceActionListeners
export const legacyInsuranceListenerSelector = (state: AppState): boolean | undefined =>
  state.insuranceMember.insuranceActionListeners?.isLegacy
export const productQuoteStatusSelector = (state: AppState): ProductQuoteStatus | undefined =>
  state.insuranceMember.productQuoteStatus
